import { useState, useEffect, useCallback, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@shadcn/components/ui/form";
import { getFieldHelperText } from "@forms/form-render/helpers/getFieldHelperText";
import { Input } from "@shadcn/components/ui/input";
import { Button } from "@shadcn/components/ui/button";
import { Loader2, Shield, Search } from "lucide-react";
import { insuranceSearchApi, InsuranceResult } from "./api";
import { InsuranceSearchProps } from "./InsuranceSearch.types";

export const InsuranceSearch = ({ name, required, disabled, helperText }: InsuranceSearchProps) => {
  const description = getFieldHelperText(helperText, required);
  const { control, setValue, watch } = useFormContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState<InsuranceResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const performSearch = useCallback(
    async (licencePlate: string) => {
      if (!licencePlate || licencePlate.trim().length === 0) {
        setError(null);
        setSelectedInsurance(null);
        setHasSearched(false);
        setManualMode(false);
        return;
      }

      setIsSearching(true);
      setError(null);
      setHasSearched(true);
      setManualMode(false);
      
      try {
        const response = await insuranceSearchApi.search({ licencePlate: licencePlate.trim().toUpperCase() });
        if (response.result) {
          setSelectedInsurance(response.result);
          setError(null);
          
          // Populate form fields with insurance data
          const data = response.result.data;
          Object.keys(data).forEach((key) => {
            setValue(`${name}.${key}`, data[key]);
          });
        } else {
          setSelectedInsurance(null);
          setError("No insurance information found for this registration plate. You can enter the details manually below.");
        }
      } catch (error: any) {
        console.error("Insurance search error:", error);
        setSelectedInsurance(null);
        setError(error?.response?.data?.message || "Failed to search for insurance information. Please try again.");
      } finally {
        setIsSearching(false);
      }
    },
    [name, setValue]
  );

  const handleSearch = () => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      return;
    }
    performSearch(searchQuery);
  };

  const handleManualMode = () => {
    setManualMode(true);
    setSelectedInsurance(null);
    setError(null);

    // Clear form fields
    setValue(`${name}.insurerName`, "");
    setValue(`${name}.abicode`, "");
    setValue(`${name}.licencePlate`, "");
    setValue(`${name}.make`, "");
    setValue(`${name}.model`, "");
    setValue(`${name}.colour`, "");
  };

  const handleSearchAgain = () => {
    setSelectedInsurance(null);
    setError(null);
    setSearchQuery("");
    setHasSearched(false);
    setManualMode(false);
    
    // Clear form fields
    setValue(`${name}.insurerName`, "");
    setValue(`${name}.abicode`, "");
    setValue(`${name}.licencePlate`, "");
    setValue(`${name}.make`, "");
    setValue(`${name}.model`, "");
    setValue(`${name}.colour`, "");
  };

  // Check for existing insurance data on mount
  const watchedInsurerName = watch(`${name}.insurerName`);
  const watchedLicencePlate = watch(`${name}.licencePlate`);
  const hasExistingInsurance = (watchedInsurerName && watchedInsurerName.trim() !== "") || 
                                (watchedLicencePlate && watchedLicencePlate.trim() !== "");
  
  useEffect(() => {
    if (hasExistingInsurance && !manualMode && selectedInsurance === null) {
      setManualMode(true);
    }
  }, [hasExistingInsurance, manualMode, selectedInsurance]);

  const showInsuranceDetails = (manualMode || selectedInsurance !== null || hasExistingInsurance);
  const showManualButton = hasSearched && !selectedInsurance && !isSearching && error !== null;

  const insurerName = selectedInsurance?.data?.insurerName || watchedInsurerName || "Unknown";

  return (
    <div className="space-y-4">
      {!showInsuranceDetails && (
        <div ref={containerRef} className="space-y-3">
          <FormField
            control={control}
            name={`${name}.licencePlate`}
            rules={
              required
                ? {
                    validate: () => {
                      if (!showInsuranceDetails && !searchQuery) {
                        return "Please enter a registration plate";
                      }
                      if (hasSearched && !selectedInsurance && !error && !isSearching) {
                        return "Please enter a valid registration plate";
                      }
                      return true;
                    },
                  }
                : undefined
            }
            render={() => (
              <FormItem>
                <FormControl>
                  <div className="flex gap-2">
                    <div className="relative max-w-[200px]">
                      <Input
                        id="insurance-search"
                        type="text"
                        placeholder="Enter registration plate..."
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value.toUpperCase());
                          setError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearch();
                          }
                        }}
                        disabled={disabled || isSearching}
                        className="pr-10"
                      />
                      {isSearching && (
                        <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                      )}
                      {!isSearching && searchQuery && (
                        <Shield className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={handleSearch}
                      disabled={disabled || isSearching || !searchQuery || searchQuery.trim().length === 0}
                      variant="default"
                    >
                      {isSearching ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="mr-2 h-4 w-4" />
                          Search
                        </>
                      )}
                    </Button>
                  </div>
                </FormControl>
                {description && <FormDescription>{description}</FormDescription>}
                <FormMessage />
                {error && !isSearching && (
                  <p className="text-sm text-destructive mt-1">{error}</p>
                )}
              </FormItem>
            )}
          />

          {showManualButton && (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleManualMode}
                disabled={disabled}
              >
                Enter manually
              </Button>
            </div>
          )}
        </div>
      )}

      {showInsuranceDetails && (
        <>
          <div className="flex items-center justify-end mb-4">
            <Button type="button" variant="outline" size="sm" onClick={handleSearchAgain} disabled={disabled}>
              Search again
            </Button>
          </div>
          {selectedInsurance && (
            <div className="rounded-md border bg-muted/50 p-4 space-y-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Registration Plate</p>
                  <p className="text-sm">{searchQuery || "ÔÇö"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Insurer</p>
                  <p className="text-sm">{insurerName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Make</p>
                  <p className="text-sm">{selectedInsurance.data?.make || "ÔÇö"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Model</p>
                  <p className="text-sm">{selectedInsurance.data?.model || "ÔÇö"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Colour</p>
                  <p className="text-sm">{selectedInsurance.data?.colour || "ÔÇö"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Year</p>
                  <p className="text-sm">{selectedInsurance.data?.dateOfFirstRegistration || selectedInsurance.data?.yearOfManufacture || "ÔÇö"}</p>
                </div>
              </div>
            </div>
          )}
          {manualMode && (
            <div className="space-y-4">
              <FormField
                control={control}
                name={`${name}.insurerName`}
                rules={required ? { required: "Insurer name is required" } : undefined}
                render={({ field }) => (
                  <FormItem>
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Insurer Name
                    </label>
                    <FormControl>
                      <Input {...field} disabled={disabled} placeholder="Enter insurer name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name={`${name}.licencePlate`}
                  rules={required ? { required: "Registration plate is required" } : undefined}
                  render={({ field }) => (
                    <FormItem>
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Registration Plate
                      </label>
                      <FormControl>
                        <Input {...field} disabled={disabled} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${name}.colour`}
                  rules={required ? { required: "Colour is required" } : undefined}
                  render={({ field }) => (
                    <FormItem>
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Colour
                      </label>
                      <FormControl>
                        <Input {...field} disabled={disabled} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name={`${name}.make`}
                  rules={required ? { required: "Make is required" } : undefined}
                  render={({ field }) => (
                    <FormItem>
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Make
                      </label>
                      <FormControl>
                        <Input {...field} disabled={disabled} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${name}.model`}
                  rules={required ? { required: "Model is required" } : undefined}
                  render={({ field }) => (
                    <FormItem>
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Model
                      </label>
                      <FormControl>
                        <Input {...field} disabled={disabled} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Hidden field for abicode if needed */}
              <FormField control={control} name={`${name}.abicode`} render={({ field }) => <Input type="hidden" {...field} />} />
            </div>
          )}
        </>
      )}
    </div>
  );
};
